import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';
import { Modal } from '../albums/Modal';

/** What the room says a boost costs here. Absent when none can be bought. */
export interface BoostPrices {
  basic_cents: number;
  priority_cents: number;
}

/**
 * How much better each tier is than free, for the label.
 *
 * Taken from `Weighting` on the server, where the multipliers are defined. If
 * they ever move, this is the line that has to move with them - which is why it
 * is one line and named after the thing it mirrors.
 */
const MULTIPLIER = { basic: '3×', priority: '8×' } as const;

/**
 * Tier order, matching `Weighting`. Money can only ever buy upwards.
 *
 * Read through `rank()` rather than indexed directly: the build runs with
 * `noUncheckedIndexedAccess`, which is right - `tier` arrives as a string off
 * the wire and a tier this table has never heard of is a real possibility.
 * Treating an unknown one as `free` would offer to sell a boost on it, so it
 * sorts above everything purchasable instead and offers nothing.
 */
const RANK: Record<string, number> = { free: 0, basic: 1, priority: 2, earned: 3, pick: 4 };

const rank = (tier: string): number => RANK[tier] ?? Number.MAX_SAFE_INTEGER;

/**
 * Paying to move your own track up a host's queue.
 *
 * ## It appears on a row, not on a room
 *
 * The thing being bought is one submission's position, so the control belongs on
 * that submission. It also means the two questions a buyer has - which track,
 * and how far up - are both answered by where they clicked.
 *
 * ## A dialog rather than a popover
 *
 * The tip button next to it is a popover, and this deliberately is not. Two
 * reasons, and the second is the one that decided it.
 *
 * The first is weight: a tip is one amount and one button, while this is a
 * choice between two prices that change somebody's standing in a queue other
 * people are also paying into. It deserves the interruption.
 *
 * The second is that it does not fit. This button lives inside the up-next
 * strip, which scrolls horizontally - and `overflow-x: auto` computes
 * `overflow-y` to `auto` as well, so anything absolutely positioned inside it is
 * clipped. The popover version opened correctly, reported itself visible to the
 * tests, and could not be seen on screen.
 *
 * ## Only upgrades are offered
 *
 * The options are filtered by rank against the tier the row already holds, so a
 * `basic` row is offered priority alone and a `priority` row is offered nothing.
 * The server refuses the rest anyway - `not_an_upgrade` - and a button whose
 * only outcome is a refusal is a worse explanation of the rule than no button.
 *
 * `earned` and `pick` outrank everything purchasable, on purpose. See
 * `Weighting`: a queue where money beats being good at finding music is not the
 * queue this platform says it runs.
 */
export function BoostButton({
  username,
  queueId,
  tier,
  prices,
  onDone,
}: {
  /** The host whose room the queue belongs to. */
  username: string;
  queueId: number;
  /** The row's current tier. */
  tier: string;
  prices: BoostPrices;
  /** Called when the dialog is dismissed without buying. */
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);

  const buy = useMutation({
    mutationFn: (want: 'basic' | 'priority') =>
      api.post<{ checkout_url: string }>(`/users/${username}/live/queue/${queueId}/boost`, {
        tier: want,
      }),
    /* A full navigation, for the reason on `TipButton`: a blocked popup loses
       the payment with no way back to it. */
    onSuccess: (result) => {
      window.location.href = result.checkout_url;
    },
  });

  const held = rank(tier);

  const options = (['basic', 'priority'] as const)
    .filter((option) => rank(option) > held)
    .map((option) => ({
      tier: option,
      cents: option === 'priority' ? prices.priority_cents : prices.basic_cents,
      label: option === 'priority' ? 'Priority' : 'Boost',
      weight: MULTIPLIER[option],
    }));

  if (options.length === 0) {
    return null;
  }

  const close = () => {
    setOpen(false);
    onDone?.();
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        aria-label="Boost this submission"
        className="px-2 py-1 text-[11px]"
        data-boost-button={queueId}
      >
        Boost
      </Button>

      {open && (
        <Modal title="Move it up the queue" onClose={close}>
          <div className="flex flex-col gap-4" data-boost-menu>
            <p className="text-sm text-soft">
              A boost adds weight to how this queue sorts. The host keeps most of
              what you pay. The rest covers processing and the platform.
            </p>

            <div className="flex flex-col gap-2">
              {options.map((option) => (
                <Button
                  key={option.tier}
                  variant="secondary"
                  full
                  disabled={buy.isPending}
                  onClick={() => buy.mutate(option.tier)}
                  data-boost-tier={option.tier}
                >
                  {/*
                    Spread by the label, not by `justify-between` on the button.

                    `Button`'s own base sets `justify-center`, and passing a
                    competing utility through `className` does not reliably win:
                    both are single-class utilities, so which applies is decided
                    by their order in Tailwind's generated stylesheet rather than
                    in the attribute. It drew two centred strings and looked
                    like the buttons had lost their styling entirely.

                    A `flex-1` label takes the slack whatever the button's own
                    alignment says.
                  */}
                  <span className="flex-1 text-left">{option.label}</span>
                  <span className="font-mono text-[12px] text-soft">
                    {option.weight} weight · ${(option.cents / 100).toFixed(2)}
                  </span>
                </Button>
              ))}
            </div>

            {buy.isError && (
              <p className="text-[13px] text-danger">
                That did not go through. The track may have already played, or
                this host may not be set up to take boosts.
              </p>
            )}

            {/* The honest version of what the money buys, said before it is
                spent rather than in a help page afterwards. */}
            <p className="text-xs text-faint">
              Weight, not a place in line. The host still runs the room, their own
              pick outranks anything bought, and an earned boost outranks both.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}
